/**
 * Product Default Card JavaScript
 *
 * Follows the exact pattern from product-card.js for consistency and reliability.
 * Handles gallery navigation, swatch interactions, and variant updates.
 *
 * Uses declarative event system (on: attributes) for event binding.
 */

import { ThemeEvents, VariantUpdateEvent, VariantSelectedEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import { debounce } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { morph } from '@theme/morph';

/**
 * Main Product Default Card Component
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {HTMLAnchorElement} [cardGalleryLink] - The gallery link element.
 * @property {HTMLAnchorElement} [productTitleLink] - The title link element.
 *
 * @extends {Component<Refs>}
 */
class ProductDefaultCard extends Component {
  requiredRefs = ['productCardLink'];


  /** @type {boolean} - Track if navigation arrows were used (disables card hover) */
  #navigationUsed = false;

  /** @type {boolean} - Track if a swatch was selected/clicked (disables card hover) */
  #swatchSelected = false;

  /** @type {string | null} - Currently hovered swatch media ID */
  #hoveredSwatchMediaId = null;

  /** @type {string | null} - Currently selected variant ID */
  #selectedVariantId = null;

  /** @type {boolean} - Track if gallery navigation has been initialized to prevent duplicates */
  #galleryNavigationInitialized = false;

  /** @type {Map<HTMLElement, Function>} - Store navigation button click handlers for cleanup */
  #navigationHandlers = new Map();

  /** @type {Map<HTMLElement, Function>} - Store dot click handlers for cleanup */
  #dotHandlers = new Map();

  /** @type {{ start: EventListener, move: EventListener, end: EventListener } | null} - Store touch swipe handlers for cleanup */
  #touchHandlers = null;

  /** @type {boolean} - Track if add to cart request is in progress to prevent multiple API calls */
  #addToCartInProgress = false;

  /** @type {number | null} - Timeout to reset add to cart in progress flag if API call hangs */
  #addToCartTimeout = null;

  /** @type {Map<HTMLButtonElement, { checkmarkTimeout: number, resetTimeout: number, restoreTimeout: number | null }>} - Store active animations to cancel on error */
  #activeAnimations = new Map();

  /** @type {number | null} - Timeout for auto-hiding error message */
  #errorMessageTimeout = null;

  get productPageUrl() {
    return this.refs.productCardLink?.href || '';
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize selected variant from default visible slide
    this.#initializeSelectedVariant();

    // Initialize gallery navigation
    this.#initializeGalleryNavigation();
    
    // Initialize card hover behavior
    this.#initializeCardHover();

    // Listen for variant updates from the swatch picker
    // Use capture phase to stop events before they reach section-level listeners
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateCapture, true);
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelectedCapture, true);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    
    // Listen for cart add events to open cart drawer
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // Listen for cart errors to show error messages
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
    
    // Setup add to cart checkmark animation
    this.#setupAddToCartCheckmarkAnimation();
    
    // Setup form submission debounce to prevent multiple API calls
    this.#setupFormSubmissionDebounce();
  }

  /**
   * Initialize selected variant from default visible slide
   * Handles out of stock products where all slides might be hidden initially
   */
  #initializeSelectedVariant() {
    const slides = this.slides;
    if (slides.length === 0) return;

    // Strategy: Find the best slide to show
    // Priority: 1) Visible + active, 2) Visible, 3) Active (even if hidden), 4) First slide
    let defaultSlide = null;

    // Priority 1: Find slide that's both visible AND has active class
    defaultSlide = slides.find(slide => 
      !slide.hidden && slide.classList.contains('card-gallery-default__slide--active')
    );

    // Priority 2: Find any visible slide (regardless of class)
    if (!defaultSlide) {
      defaultSlide = slides.find(slide => !slide.hidden);
    }

    // Priority 3: Find first slide with active class (even if hidden) - handles out of stock products
    if (!defaultSlide) {
      defaultSlide = slides.find(slide => 
        slide.classList.contains('card-gallery-default__slide--active')
      );
    }

    // Priority 4: Use the first slide as fallback - ensures at least one image shows
    if (!defaultSlide) {
      defaultSlide = slides[0];
    }

    // Ensure the default slide is visible
    if (defaultSlide) {
      // Clear all slides first - remove all state classes and hide them
      slides.forEach(slide => {
        slide.classList.remove(
          'card-gallery-default__slide--active',
          'card-gallery-default__slide--swatch-selected',
          'card-gallery-default__slide--swatch-hovered',
          'card-gallery-default__slide--card-hover'
        );
        slide.hidden = true;
      });

      // Show the default slide
      defaultSlide.hidden = false;
      defaultSlide.classList.add('card-gallery-default__slide--active');

      // Set selected variant ID
      if (defaultSlide.dataset.variantId) {
        this.#selectedVariantId = defaultSlide.dataset.variantId;
      }

      // Update dots if they exist
      const variantId = defaultSlide.dataset.variantId;
      const dots = this.querySelectorAll('.card-gallery-default__dot');
      if (variantId && dots.length > 0) {
        this.#updateDotsForVariant(variantId);
      }
    }
  }

  /**
   * Public method to re-initialize the gallery
   * Called after filter updates to ensure all cards are properly initialized
   * Removes old event listeners before adding new ones to prevent duplicates
   */
  reinitializeGallery() {
    if (this.gallery && this.slides.length > 0) {
      // CRITICAL: Remove old event listeners before re-initializing to prevent duplicates
      this.#removeGalleryNavigationListeners();
      
      this.#initializeSelectedVariant();
      this.#initializeGalleryNavigation();
    }
  }

  /**
   * Remove gallery navigation event listeners to prevent duplicates
   * Called before re-initialization after filter/pagination updates
   */
  #removeGalleryNavigationListeners() {
    const gallery = this.gallery;
    if (!gallery) return;

    // Remove navigation arrow handlers
    this.#navigationHandlers.forEach((handler, element) => {
      element.removeEventListener('click', /** @type {EventListener} */ (handler));
    });
    this.#navigationHandlers.clear();

    // Remove dot click handlers
    this.#dotHandlers.forEach((handler, element) => {
      element.removeEventListener('click', /** @type {EventListener} */ (handler));
    });
    this.#dotHandlers.clear();

    // Remove touch swipe handlers
    if (this.#touchHandlers) {
      const mediaWrapper = gallery.querySelector('.card-gallery-default__media-wrapper');
      if (mediaWrapper) {
        mediaWrapper.removeEventListener('touchstart', /** @type {EventListener} */ (this.#touchHandlers.start));
        mediaWrapper.removeEventListener('touchmove', /** @type {EventListener} */ (this.#touchHandlers.move));
        mediaWrapper.removeEventListener('touchend', /** @type {EventListener} */ (this.#touchHandlers.end));
      }
      this.#touchHandlers = null;
    }

    // Reset initialization flag
    this.#galleryNavigationInitialized = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateCapture, true);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
    
    // Clean up gallery navigation event listeners
    this.#removeGalleryNavigationListeners();
  }

  /**
   * Initialize gallery navigation (arrows and dots)
   * Tracks event handlers for cleanup to prevent duplicate listeners
   */
  #initializeGalleryNavigation() {
    const gallery = this.gallery;
    if (!gallery) return;

    // Prevent duplicate initialization
    if (this.#galleryNavigationInitialized) {
      return;
    }

    // Bind arrow navigation with handler tracking
    const prevButton = gallery.querySelector('.card-gallery-default__arrow--prev');
    const nextButton = gallery.querySelector('.card-gallery-default__arrow--next');

    if (prevButton instanceof HTMLElement) {
      /** @type {EventListener} */
      const prevHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#navigateGallery('prev');
      };
      prevButton.addEventListener('click', prevHandler);
      this.#navigationHandlers.set(prevButton, prevHandler);
    }

    if (nextButton instanceof HTMLElement) {
      /** @type {EventListener} */
      const nextHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#navigateGallery('next');
      };
      nextButton.addEventListener('click', nextHandler);
      this.#navigationHandlers.set(nextButton, nextHandler);
    }

    // Bind dot navigation (variant-specific) with handler tracking
    // Uses array index instead of data-image-index to prevent skipping slides
    const dots = gallery.querySelectorAll('.card-gallery-default__dot');
    dots.forEach((dot, dotIndex) => {
      if (!(dot instanceof HTMLElement)) return;
      
      /** @type {EventListener} */
      const dotHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Mark navigation as used - this disables card hover logic
        this.#navigationUsed = true;
        this.#clearSwatchHoverState();
        
        // Get selected variant ID
        const variantId = this.#getSelectedVariantId();
        if (!variantId) return;

        // Get slides for this variant only
        const variantSlides = this.#getSlidesForVariant(variantId);
        
        // Use dot's position in the dots array (dotIndex) to get corresponding slide
        // This ensures dots map directly to variant slides by array position
        const targetSlide = variantSlides[dotIndex];

        if (targetSlide) {
          this.#showSlide(targetSlide);
        }
      };
      dot.addEventListener('click', dotHandler);
      this.#dotHandlers.set(dot, dotHandler);
    });

    // Initialize touch swipe for mobile
    this.#initializeTouchSwipe(gallery);

    // Mark as initialized
    this.#galleryNavigationInitialized = true;
  }

  /**
   * Initialize touch swipe navigation for mobile
   * Stores handlers for cleanup to prevent duplicate listeners
   * @param {HTMLElement} gallery
   */
  #initializeTouchSwipe(gallery) {
    let touchStartX = 0;
    let touchStartY = 0;
    let deltaX = 0;
    let deltaY = 0;
    const minSwipeDistance = 50;

    /** @type {EventListener} */
    const handleTouchStart = (e) => {
      const touchEvent = /** @type {TouchEvent} */ (e);
      if (touchEvent.touches.length !== 1) return;
      const touch = touchEvent.touches[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
    };

    /** @type {EventListener} */
    const handleTouchMove = (e) => {
      const touchEvent = /** @type {TouchEvent} */ (e);
      if (touchEvent.touches.length !== 1) return;
      const touch = touchEvent.touches[0];
      if (!touch) return;
      deltaX = touch.clientX - touchStartX;
      deltaY = touch.clientY - touchStartY;
      // Prevent scroll for horizontal swipes
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        e.preventDefault();
      }
    };

    /** @type {EventListener} */
    const handleTouchEnd = () => {
      // Check if valid horizontal swipe
      if (Math.abs(deltaX) < minSwipeDistance || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      const variantId = this.#getSelectedVariantId();
      if (!variantId) return;

      const variantSlides = this.#getSlidesForVariant(variantId);
      if (variantSlides.length <= 1) return;

      this.#navigationUsed = true;
      this.#clearSwatchHoverState();
      this.#navigateGallery(deltaX > 0 ? 'prev' : 'next');
    };

    const mediaWrapper = gallery.querySelector('.card-gallery-default__media-wrapper');
    if (mediaWrapper) {
      mediaWrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
      mediaWrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
      mediaWrapper.addEventListener('touchend', handleTouchEnd, { passive: true });

      // Store handlers for cleanup
      this.#touchHandlers = {
        start: handleTouchStart,
        move: handleTouchMove,
        end: handleTouchEnd
      };
    }
  }

  /**
   * Initialize card hover behavior for showing secondary image
   */
  #initializeCardHover() {
    // Only enable card hover on desktop (1200px+) - disable on tablet/mobile (up to 1199px)
    if (window.innerWidth < 1200) {
      return; // Don't initialize card hover on tablet/mobile
    }
    
    // Add mouseenter and mouseleave listeners to product card
    this.addEventListener('mouseenter', this.#handleCardHoverEnter);
    this.addEventListener('mouseleave', this.#handleCardHoverLeave);
  }

  /**
   * Handle card hover enter - show secondary image of current/selected variant
   * Uses array indices instead of data-image-index to prevent skipping slides
   */
  #handleCardHoverEnter = () => {
    // Disable card hover on tablet/mobile (up to 1199px) - same as mobile view
    if (window.innerWidth < 1200) {
      return;
    }
    
    // Don't show hover if swatch is being hovered (swatch hover takes priority)
    if (this.#hoveredSwatchMediaId) {
      return;
    }

    // Allow card hover even after navigation - clear navigation flag on hover
    if (this.#navigationUsed) {
      this.#navigationUsed = false;
    }

    // Get selected variant ID
    const variantId = this.#getSelectedVariantId();
    if (!variantId) return;

    // Get slides for this variant only
    const variantSlides = this.#getSlidesForVariant(variantId);
    if (variantSlides.length < 2) return; // Need at least 2 images to show secondary

    // Find the currently visible slide's index in the variant slides array
    const currentSlideIndex = variantSlides.findIndex(slide => !slide.hidden && (
      slide.classList.contains('card-gallery-default__slide--active') ||
      slide.classList.contains('card-gallery-default__slide--swatch-selected')
    ));
    
    if (currentSlideIndex === -1) return;

    // Calculate next slide index using array position (not data-image-index)
    const nextSlideIndex = (currentSlideIndex + 1) % variantSlides.length;
    
    // Get next slide directly from array by index
    const nextSlide = variantSlides[nextSlideIndex];

    if (nextSlide) {
      // Add card-hover class to next slide
      nextSlide.classList.add('card-gallery-default__slide--card-hover');
      nextSlide.hidden = false;
    }
  };

  /**
   * Handle card hover leave - remove secondary image hover state
   */
  #handleCardHoverLeave = () => {
    // Remove card-hover class from all slides
    this.slides.forEach(slide => {
      slide.classList.remove('card-gallery-default__slide--card-hover');
    });
  };

  /**
   * Navigate gallery (prev/next) - only within selected variant's images
   * Uses array indices instead of data-image-index to prevent skipping slides
   * @param {string} direction
   */
  #navigateGallery(direction) {
    // Get selected variant ID
    const variantId = this.#getSelectedVariantId();
    if (!variantId) return;

    // Get slides for this variant only
    const variantSlides = this.#getSlidesForVariant(variantId);
    if (variantSlides.length === 0) return;

    // Mark navigation as used - this disables card hover logic
    this.#navigationUsed = true;
    this.#clearSwatchHoverState();

    // Find current slide's index within variant slides array
    const currentSlideIndex = variantSlides.findIndex(slide => !slide.hidden);
    if (currentSlideIndex === -1) return;

    // Calculate new index based on array position (not data-image-index)
    let newSlideIndex;

    if (direction === 'next') {
      newSlideIndex = (currentSlideIndex + 1) % variantSlides.length;
    } else {
      newSlideIndex = (currentSlideIndex - 1 + variantSlides.length) % variantSlides.length;
    }

    // Get slide directly from array by index
    const nextSlide = variantSlides[newSlideIndex];

    if (nextSlide) {
      this.#showSlide(nextSlide);
    }
  }

  /**
   * Get current slide index
   * @returns {number}
   */
  #getCurrentSlideIndex() {
    const slides = this.slides;
    return slides.findIndex((slide) => !slide.hidden);
  }

  /**
   * Show slide by index
   * @param {number} index
   */
  #showSlideByIndex(index) {
    const slides = this.slides;
    if (index < 0 || index >= slides.length) return;

    const slideToShow = slides[index];
    if (slideToShow) {
      // Clear swatch hover state when navigating manually
      this.#clearSwatchHoverState();
      this.#showSlide(slideToShow);
    }
  }

  /**
   * Show slide by media ID
   * @param {string} mediaId
   * @param {boolean} [isSelected=false] - Whether this is from a selected swatch (not hover)
   */
  #showSlideByMediaId(mediaId, isSelected = false) {
    if (!mediaId) {
      // If no media ID provided, show first slide as fallback
      this.#showSlideByIndex(0);
      return;
    }

    const slideToShow = this.slides.find((slide) => String(slide.dataset.mediaId) === String(mediaId));

    if (slideToShow) {
      // Clear all slides first (but don't hide if they're the first slide and no selection was made)
      this.slides.forEach((slide) => {
        slide.classList.remove(
          'card-gallery-default__slide--active',
          'card-gallery-default__slide--swatch-selected',
          'card-gallery-default__slide--swatch-hovered',
          'card-gallery-default__slide--card-hover'
        );
        // Only hide if this is a selection or navigation action
        if (isSelected || this.#navigationUsed) {
          slide.hidden = true;
        }
      });

      // Show the target slide with appropriate state class
      slideToShow.hidden = false;
      slideToShow.classList.add('card-gallery-default__slide--active');
      
      if (isSelected) {
        slideToShow.classList.add('card-gallery-default__slide--swatch-selected');
      }

      // Update dots
      const dots = this.querySelectorAll('.card-gallery-default__dot');
      const slideIndex = parseInt(slideToShow.dataset.slideIndex || '0', 10);
      dots.forEach((dot, index) => {
        // If slide index is beyond visible dots, show last dot as active
        const activeIndex = slideIndex < dots.length ? slideIndex : dots.length - 1;
        const isActive = index === activeIndex;
        dot.classList.toggle('card-gallery-default__dot--active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    } else {
      // If slide with media ID not found, keep showing current slide or show first slide
      // This prevents hiding all images when variant doesn't have assigned image
      const currentSlide = this.slides.find(slide => !slide.hidden);
      if (!currentSlide) {
        // No current slide visible, show first slide as fallback
        this.#showSlideByIndex(0);
      }
      // Otherwise, keep current slide visible (do nothing)
    }
  }

  /**
   * Show a specific slide
   * @param {HTMLElement} slideElement
   */
  #showSlide(slideElement) {
    // Clear all slide state classes
    this.slides.forEach((slide) => {
      slide.classList.remove(
        'card-gallery-default__slide--active',
        'card-gallery-default__slide--swatch-selected',
        'card-gallery-default__slide--swatch-hovered',
        'card-gallery-default__slide--card-hover'
      );
      slide.hidden = true;
    });

    // Show the target slide
    slideElement.classList.add('card-gallery-default__slide--active');
    slideElement.hidden = false;

    // Update selected variant ID if slide has variant ID
    if (slideElement.dataset.variantId) {
      this.#selectedVariantId = slideElement.dataset.variantId;
    }

    // Update dots for variant
    const variantId = slideElement.dataset.variantId;
    if (variantId) {
      this.#updateDotsForVariant(variantId);
    } else {
      // Fallback to old behavior
      const dots = this.querySelectorAll('.card-gallery-default__dot');
      const slideIndex = parseInt(slideElement.dataset.slideIndex || '0', 10);
      dots.forEach((dot, index) => {
        const activeIndex = slideIndex < dots.length ? slideIndex : dots.length - 1;
        const isActive = index === activeIndex;
        dot.classList.toggle('card-gallery-default__dot--active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }
  }

  /**
   * Get the variant picker component
   * @returns {SwatchesDefaultVariantPickerComponent | null}
   */
  get variantPicker() {
    return this.querySelector('swatches-default-variant-picker-component');
  }

  /**
   * Get the gallery element
   * @returns {HTMLElement | null}
   */
  get gallery() {
    return this.querySelector('.card-gallery-default');
  }

  /**
   * Get all gallery slides
   * @returns {HTMLElement[]}
   */
  get slides() {
    return Array.from(this.querySelectorAll('.card-gallery-default__slide'));
  }

  /**
   * Get slides for a specific variant ID
   * @param {string} variantId
   * @returns {HTMLElement[]}
   */
  #getSlidesForVariant(variantId) {
    if (!variantId) return [];
    return this.slides.filter(slide => String(slide.dataset.variantId) === String(variantId));
  }

  /**
   * Get currently selected variant ID (from selected swatch or default)
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    if (this.#selectedVariantId) {
      return this.#selectedVariantId;
    }
    
    // Get from selected swatch
    const selectedSwatch = this.querySelector('.swatches-default__swatch--selected input[type="radio"]:checked');
    if (selectedSwatch && selectedSwatch.dataset.variantId) {
      return selectedSwatch.dataset.variantId;
    }
    
    // Get from default visible slide
    const defaultSlide = this.slides.find(slide => 
      !slide.hidden && slide.classList.contains('card-gallery-default__slide--active')
    );
    if (defaultSlide && defaultSlide.dataset.variantId) {
      return defaultSlide.dataset.variantId;
    }
    
    return null;
  }

  /**
   * Handle variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  /**
   * Stop VariantSelectedEvent from OTHER cards in capture phase
   * @param {VariantSelectedEvent} event
   */
  #handleVariantSelectedCapture = (event) => {
    // Stop events from OTHER cards in capture phase before they reach section-level listeners
    const eventTarget = event.target;
    const isEventFromThisCard = eventTarget && this.contains(/** @type {Node} */ (eventTarget));
    
    // If event is not from this card, stop it immediately in capture phase
    if (!isEventFromThisCard) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    
    // For events from THIS card, allow them to continue to bubble phase
  };

  /**
   * Handle variant selected event
   */
  #handleVariantSelected = (event) => {
    // Stop propagation to prevent event from reaching section level where other cards' components listen
    event.stopPropagation();
    
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handle cart add event - open cart drawer
   * @param {CartAddEvent} event
   */
  #handleCartAdd = (event) => {
    // Check if this add-to-cart came from this product card
    const productFormComponent = this.querySelector('product-form-component');
    if (!productFormComponent) return;
    
    // Match by product ID instead of source ID (sourceId is variant ID, not form element ID)
    const eventProductId = event.detail?.data?.productId;
    const cardProductId = productFormComponent instanceof HTMLElement ? productFormComponent.dataset.productId : '';
    
    if (cardProductId && eventProductId && eventProductId !== cardProductId) return;
    
    const eventSource = event.detail?.data?.source;
    if (eventSource && eventSource !== 'product-form-component') return;
    
    // Reset add to cart in progress flag on successful add
    this.#addToCartInProgress = false;
    if (this.#addToCartTimeout !== null) {
      clearTimeout(this.#addToCartTimeout);
      this.#addToCartTimeout = null;
    }
    
    // Cart drawer will open automatically via auto-open attribute on cart-drawer-component
    // when CartAddEvent is dispatched (handled by product-form-component)
  };

  /**
   * Check if element is in quick view or popup context (should not be handled by product card)
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  #isInOtherContext(element) {
    if (!element) return false;
    return !!(
      element.closest('quick-view-component') ||
      element.closest('drawer-items-component') ||
      element.closest('[data-drawer-footer]') ||
      element.closest('collection-banner-popup-component')
    );
  }

  /**
   * Handle cart error event - show error message
   * @param {CustomEvent} event
   */
  #handleCartError = (event) => {
    if (!(event instanceof CartErrorEvent)) return;
    
    const sourceId = event.detail?.sourceId;
    
    // Check if error is from quick view, popup, or other contexts - if so, ignore it
    if (sourceId) {
      const form = document.getElementById(sourceId);
      if (form) {
        // If form is in other context or not in this product card, don't handle it
        if (this.#isInOtherContext(form) || !this.contains(form)) {
          return;
        }
      }
    }
    
    // Extract error message
    let errorMessage = event.detail?.data?.message || 'Unable to add item to cart';
    const errors = event.detail?.data?.errors;
    
    if (errors) {
      if (errors.items) {
        const itemsError = Array.isArray(errors.items) ? errors.items[0] : errors.items;
        if (itemsError) {
          errorMessage = typeof itemsError === 'string' ? itemsError : (itemsError.message || errorMessage);
        }
      } else if (errors.quantity) {
        const quantityError = Array.isArray(errors.quantity) ? errors.quantity[0] : errors.quantity;
        if (quantityError) {
          errorMessage = typeof quantityError === 'string' ? quantityError : (quantityError.message || errorMessage);
        }
      } else if (Array.isArray(errors) && errors.length > 0) {
        errorMessage = typeof errors[0] === 'string' ? errors[0] : (errors[0].message || errorMessage);
      } else if (errors.message) {
        errorMessage = errors.message;
      }
    }
    
    // Replace specific error phrases with custom message
    const lowerMessage = errorMessage.toLowerCase();
    if (lowerMessage.includes('parameter missing') || lowerMessage.includes('invalid')) {
      errorMessage = 'The maximum quantity of this item is already in your cart.';
    }
    
    // Find button - only from forms within this product card
    let addToCartButton = null;
    if (sourceId) {
      const form = document.getElementById(sourceId);
      if (form && this.contains(form)) {
        addToCartButton = form.querySelector('button[type="submit"][name="add"]');
      }
    }
    
    // Fallback: find processing button in this product card
    if (!addToCartButton) {
      addToCartButton = Array.from(this.querySelectorAll('button[type="submit"][name="add"]'))
        .find(btn => btn.classList.contains('add-to-cart-button--processing'));
    }
    
    // Final validation: ensure button is in this product card and not in other contexts
    if (!addToCartButton || this.#isInOtherContext(addToCartButton) || !this.contains(addToCartButton)) {
      return;
    }
    
    // Reset add to cart in progress flag on error
    this.#addToCartInProgress = false;
    if (this.#addToCartTimeout !== null) {
      clearTimeout(this.#addToCartTimeout);
      this.#addToCartTimeout = null;
    }
    
      // Cancel any active animation
      const animationId = this.#activeAnimations.get(addToCartButton);
      if (animationId) {
        clearTimeout(animationId.checkmarkTimeout);
        clearTimeout(animationId.resetTimeout);
        if (animationId.restoreTimeout !== null) {
          clearTimeout(animationId.restoreTimeout);
        }
        this.#activeAnimations.delete(addToCartButton);
      }
    
    // Reset button state immediately
    addToCartButton.classList.remove('add-to-cart-button--processing');
    const checkmarkElement = addToCartButton.querySelector('.add-to-cart-checkmark');
    if (checkmarkElement) {
      checkmarkElement.classList.remove('add-to-cart-checkmark--visible');
    }
    
    // Restore .add-to-cart-text container
    const addToCartTextContainer = addToCartButton.querySelector('.add-to-cart-text');
    if (addToCartTextContainer) {
      addToCartTextContainer.classList.remove('add-to-cart-text--hidden');
    }
    
    // Show error message
    this.#showAddToCartError(addToCartButton, errorMessage);
};


  /**
   * Show error message above add to cart button
   * @param {HTMLButtonElement} button
   * @param {string} message
   */
  #showAddToCartError(button, message) {
    // Clear any existing error message timeout
    if (this.#errorMessageTimeout !== null) {
      clearTimeout(this.#errorMessageTimeout);
      this.#errorMessageTimeout = null;
    }
    
    // Remove ALL existing error messages within this product card to prevent duplicates
    const existingErrors = this.querySelectorAll('.add-to-cart-error-message');
    existingErrors.forEach(error => error.remove());
    
    // For desktop: find .card-gallery-default div and insert error message AFTER it
    const cardGalleryDefault = button.closest('.card-gallery-default');
    
    // For mobile: find .buy-buttons-default container
    const buyButtonsDefault = button.closest('.buy-buttons-default');
    
    let targetContainer = null;
    let insertAfter = false;
    
    if (cardGalleryDefault) {
      // Desktop: insert AFTER .card-gallery-default
      targetContainer = cardGalleryDefault;
      insertAfter = true;
    } else if (buyButtonsDefault) {
      // Mobile: insert ABOVE .buy-buttons-default
      targetContainer = buyButtonsDefault;
      insertAfter = false;
    }
    
    if (!targetContainer) {
      console.warn('ProductDefaultCard: Could not find target container for error message');
      return;
    }
    
    // Get the parent element for insertion
    const parentElement = targetContainer.parentElement;
    if (!parentElement) {
      console.warn('ProductDefaultCard: Could not find parent element for error message');
      return;
    }
    
    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.className = 'add-to-cart-error-message';
    errorElement.textContent = message;
    errorElement.setAttribute('role', 'alert');
    errorElement.setAttribute('aria-live', 'assertive');
    
    // Insert error message based on container type
    if (insertAfter) {
      // Desktop: insert AFTER .card-gallery-default div
      targetContainer.insertAdjacentElement('afterend', errorElement);
    } else {
      // Mobile: insert ABOVE .buy-buttons-default (as a sibling, before it)
      parentElement.insertBefore(errorElement, targetContainer);
    }
    
    // Auto-hide after 5 seconds
    this.#errorMessageTimeout = setTimeout(() => {
      if (errorElement.parentElement) {
        errorElement.remove();
      }
      this.#errorMessageTimeout = null;
    }, 5000);
  }

  /**
   * Setup checkmark animation for add to cart buttons
   */
  #setupAddToCartCheckmarkAnimation() {
    
    // Listen for cart errors to cancel animations and show error
    const errorHandler = (/** @type {CustomEvent} */ event) => {
      if (!(event instanceof CartErrorEvent)) return;
      
      // Find the button that triggered this error
      const sourceId = event.detail?.sourceId;
      const errorMessage = event.detail?.data?.message || 'Unable to add item to cart';
      
      // Find button by form ID or search in product card context
      let addToCartButton = null;
      if (sourceId) {
        const form = document.getElementById(sourceId);
        if (form && this.contains(form)) {
          addToCartButton = form.querySelector('button[type="submit"][name="add"]');
        }
      }
      
      // Fallback: find button in product card context
      if (!addToCartButton) {
        const buttons = this.querySelectorAll('button[type="submit"][name="add"]');
        // Find the one that's currently processing
        buttons.forEach((btn) => {
          if (btn.classList.contains('add-to-cart-button--processing')) {
            addToCartButton = btn;
          }
        });
      }
      
      // Final validation: ensure button is in this product card and not in other contexts
      if (!addToCartButton || this.#isInOtherContext(addToCartButton) || !this.contains(addToCartButton)) {
        return;
      }
      
      // Cancel any active animation
      const animationId = this.#activeAnimations.get(addToCartButton);
      if (animationId) {
        clearTimeout(animationId.checkmarkTimeout);
        clearTimeout(animationId.resetTimeout);
        if (animationId.restoreTimeout !== null) {
          clearTimeout(animationId.restoreTimeout);
        }
        this.#activeAnimations.delete(addToCartButton);
      }
      
      // Reset button state immediately
      addToCartButton.classList.remove('add-to-cart-button--processing');
      const checkmarkElement = addToCartButton.querySelector('.add-to-cart-checkmark');
      if (checkmarkElement) {
        checkmarkElement.classList.remove('add-to-cart-checkmark--visible');
      }
      
      // Restore the entire .add-to-cart-text container (which contains both icon and text)
      const addToCartTextContainer = addToCartButton.querySelector('.add-to-cart-text');
      if (addToCartTextContainer) {
        addToCartTextContainer.classList.remove('add-to-cart-text--hidden');
      }
      
      // Show error message
      this.#showAddToCartError(addToCartButton, errorMessage);
    };
    
    document.addEventListener(ThemeEvents.cartError, errorHandler);
    
    // Use event delegation to handle clicks on add-to-cart buttons
    this.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const addToCartButton = /** @type {HTMLButtonElement | null} */ (
        target.closest('button[type="submit"][name="add"]')
      );
      
      if (!addToCartButton || addToCartButton.disabled) return;
      
      // Only handle if button is within this product card
      if (!this.contains(addToCartButton) || this.#isInOtherContext(addToCartButton)) return;

      // Find the .add-to-cart-text container (contains both icon and text)
      const addToCartTextContainer = addToCartButton.querySelector('.add-to-cart-text');
      if (!addToCartTextContainer) return;

      // Check if checkmark already exists
      let checkmarkElement = /** @type {HTMLElement | null} */ (
        addToCartButton.querySelector('.add-to-cart-checkmark')
      );

      if (!checkmarkElement) {
        // Create checkmark element
        checkmarkElement = document.createElement('span');
        checkmarkElement.className = 'add-to-cart-checkmark';
        checkmarkElement.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 6L9 17l-5-5"></path>
          </svg>
        `;
        addToCartButton.appendChild(checkmarkElement);
      }

      // Hide .add-to-cart-text container (contains both icon and text)
      addToCartTextContainer.classList.add('add-to-cart-text--hidden');
      addToCartButton.classList.add('add-to-cart-button--processing');

      // Store timeout IDs to allow cancellation
      const checkmarkTimeout = setTimeout(() => {
        checkmarkElement.classList.add('add-to-cart-checkmark--visible');
      }, 300); // Checkmark appears after 0.3s

      const resetTimeout = setTimeout(() => {
        checkmarkElement.classList.remove('add-to-cart-checkmark--visible');

        const restoreTimeout = setTimeout(() => {
          addToCartButton.classList.remove('add-to-cart-button--processing');
          addToCartTextContainer.classList.remove('add-to-cart-text--hidden');
          this.#activeAnimations.delete(addToCartButton);
        }, 1000);
        
        // Update with restore timeout
        const current = this.#activeAnimations.get(addToCartButton);
        if (current) {
          current.restoreTimeout = restoreTimeout;
        }
      }, 3000);
      
      this.#activeAnimations.set(addToCartButton, {
        checkmarkTimeout,
        resetTimeout,
        restoreTimeout: null
      });
    }, true); // Use capture phase
  }

  /**
   * Setup form submission debounce to prevent multiple API calls
   */
  #setupFormSubmissionDebounce() {
    // Listen for form submit events to prevent multiple submissions
    this.addEventListener('submit', (/** @type {Event} */ event) => {
      const form = /** @type {HTMLFormElement | null} */ (event.target);
      if (!form || !form.closest('product-form-component')) return;
      
      // Check if request is already in progress
      if (this.#addToCartInProgress) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      // Set flag to prevent multiple submissions
      this.#addToCartInProgress = true;
      
      // Clear any existing timeout
      if (this.#addToCartTimeout !== null) {
        clearTimeout(this.#addToCartTimeout);
      }
      
      // Set timeout fallback to reset flag after 10 seconds (in case API call hangs)
      this.#addToCartTimeout = setTimeout(() => {
        this.#addToCartInProgress = false;
        this.#addToCartTimeout = null;
      }, 10000);
    }, true); // Use capture phase to catch before form submission
  }

  /**
   * Handle variant update event in capture phase - stops events from OTHER cards
   * For events from THIS card, we allow them to pass to bubble phase for processing
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdateCapture = (event) => {
    // CRITICAL: Stop events from OTHER cards in capture phase before they reach section-level listeners
    // Events from THIS card are allowed to continue to bubble phase for processing
    
    // First check: Is the event target within this card?
    const eventTarget = event.target;
    const isEventFromThisCard = eventTarget && this.contains(/** @type {Node} */ (eventTarget));
    
    // If event is not from this card, stop it immediately in capture phase
    // This prevents it from even reaching bubble phase or section-level listeners
    if (!isEventFromThisCard) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    
    if (!event.detail?.data) return;
    
    // Second check: Does the productId match?
    // Convert to strings for reliable comparison (productId might be number or string)
    const eventProductId = String(event.detail.data.productId || '');
    const currentProductId = String(this.dataset.productId || '');
    
    // Stop events from OTHER product cards immediately in capture phase
    // This prevents them from even reaching the bubble phase or section level
    if (eventProductId && currentProductId && eventProductId !== currentProductId) {
      // Event is for a different product card, stop it immediately
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    
    // Also stop if one has productId but the other doesn't (mismatch scenario)
    if ((eventProductId && !currentProductId) || (!eventProductId && currentProductId)) {
      // Mismatch - stop the event to prevent affecting other cards
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    
    // For events from THIS card with matching productId, allow them to continue
    // They will be processed in bubble phase and stopped there to prevent reaching section
  };

  /**
   * Handle variant update event
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (!event.detail?.data) return;

    // CRITICAL: Stop propagation IMMEDIATELY (FIRST THING) to prevent event from reaching section level
    // where other cards' product-form-components listen. This matches the standard product-card.js pattern.
    // This fixes the issue where selecting a swatch on one product disables add-to-cart buttons on all other products.
    event.stopPropagation();

    // Ensure this event is for this product card only
    // Convert to strings for reliable comparison (productId might be number or string)
    const eventProductId = String(event.detail.data.productId || '');
    const currentProductId = String(this.dataset.productId || '');
    
    // If this event is for a different product card, return early
    // (Capture phase should have already stopped these, but double-check for safety)
    if (eventProductId && currentProductId && eventProductId !== currentProductId) {
      return;
    }

    const { html } = event.detail.data;
    const variantResource = event.detail.resource;

    // Mark swatch as selected - this disables card hover logic
    this.#swatchSelected = true;
    this.#clearSwatchHoverState();

    // Update product URL
    this.#updateProductUrl(html);

    // Update variant images in gallery
    this.#updateVariantImages();

    // Update form variant IDs
    this.#updateFormVariantIds(html);

    // Update price
    this.#updatePrice(html);

    // Update badges
    this.#updateBadges(html);

    // Update shipping badge
    this.#updateShippingBadge(html);

    // Update product title
   // this.#updateProductTitle(html, variantResource);

    // Update selected swatch visual state
    this.#updateSwatchSelectedState();

    // CRITICAL: Manually update product-form-component button state
    // This ensures the button state is correct even if the event doesn't reach product-form-component
    // or if product-form-component's handler doesn't run properly
    // Use requestAnimationFrame to ensure this runs after product-form-component's handler (if it runs)
    requestAnimationFrame(() => {
      this.#updateProductFormComponent(html, variantResource);
    });

    // CRITICAL: Stop propagation AFTER processing to prevent event from reaching section level
    // where other cards' product-form-components listen. 
    // The capture phase already stops events from OTHER cards, so this only stops events from THIS card
    // from bubbling to the section level. This allows product-form-component on THIS card to receive the event.
    // NOTE: We use stopPropagation() (not stopImmediatePropagation()) so that other handlers on the same element
    // (like gallery's product-form-component) can still receive and process the event
    event.stopPropagation();
  };


  /**
   * Update product URLs
   * @param {Document} html
   */
  #updateProductUrl(html) {
    const anchorElement = html?.querySelector('product-default-card a[ref="productCardLink"]');

    if (!(anchorElement instanceof HTMLAnchorElement)) return;
    if (anchorElement.getAttribute('href')?.trim() === '') return;

    const productUrl = anchorElement.href;
    const { productCardLink, cardGalleryLink, productTitleLink } = this.refs;

    if (productCardLink instanceof HTMLAnchorElement) {
      productCardLink.href = productUrl;
    }
    if (cardGalleryLink instanceof HTMLAnchorElement) {
      cardGalleryLink.href = productUrl;
    }
    if (productTitleLink instanceof HTMLAnchorElement) {
      productTitleLink.href = productUrl;
    }
  }

  /**
   * Update variant images in gallery
   */
  #updateVariantImages() {
    if (!this.variantPicker?.selectedOption) {
      // If no variant selected, ensure first slide is visible
      const firstSlide = this.slides[0];
      if (firstSlide && firstSlide.hidden) {
        firstSlide.hidden = false;
        firstSlide.classList.add('card-gallery-default__slide--active');
      }
      return;
    }

    const selectedMediaId = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (selectedMediaId) {
      this.#showSlideByMediaId(selectedMediaId, true); // true = mark as selected
    } else {
      // If no media ID, show first slide
      const firstSlide = this.slides[0];
      if (firstSlide) {
        this.#showSlide(firstSlide);
      }
    }
  }

  /**
   * Update form variant IDs
   * @param {Document} html
   */
  #updateFormVariantIds(html) {
    const newVariantInput = html?.querySelector('input[name="id"]');
    if (!newVariantInput) return;

    const newVariantId = newVariantInput.value;

    // Update all variant ID inputs in forms (gallery form, mobile form, Shop Pay form)
    const variantInputs = this.querySelectorAll('input[name="id"]');
    variantInputs.forEach((input) => {
      input.value = newVariantId;
    });

    // Also update ref="variantId" inputs (used by product-form-component)
    const variantIdRefs = this.querySelectorAll('[ref="variantId"]');
    variantIdRefs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = newVariantId;
      }
    });
  }

  /**
   * Update price display
   * @param {Document} html
   */
  #updatePrice(html) {
    // Try to find price block in the section rendering response
    const newPrice = html?.querySelector('.price-default');
    
    if (!newPrice) {
      // If not found in section rendering, try to find it in the product-default-card wrapper
      const productCard = html?.querySelector('product-default-card');
      if (productCard) {
        const priceInCard = productCard.querySelector('.price-default');
        if (priceInCard) {
          this.#morphPriceElement(priceInCard);
          return;
        }
      }
      return;
    }

    this.#morphPriceElement(newPrice);
  }

  /**
   * Morph the price element with new content
   * @param {HTMLElement} newPrice - The new price element from the response
   */
  #morphPriceElement(newPrice) {
    const currentPrice = this.querySelector('.price-default');
    
    if (!currentPrice) return;

    // Store the settings BEFORE morphing
    const showBudgetPay = currentPrice.dataset.showBudgetPay === 'true';
    const showDiscountPercentage = currentPrice.dataset.showDiscountPercentage === 'true';

    // Store the price range element BEFORE morphing (it should remain static)
    const priceRangeElement = currentPrice.querySelector('.price-default__range');
    const priceRangeClone = priceRangeElement?.cloneNode(true);

    // IMPORTANT: Morph FIRST (morph modifies currentPrice in place)
    morph(currentPrice, newPrice);

    // Use requestAnimationFrame to ensure DOM is updated after morph
    requestAnimationFrame(() => {
      // After morphing, currentPrice IS the updated element (morph modifies in place)
      // Update data attributes to preserve settings for future updates
      currentPrice.dataset.showBudgetPay = String(showBudgetPay);
      currentPrice.dataset.showDiscountPercentage = String(showDiscountPercentage);

      // Restore price range if it existed before (price range is static and shouldn't change on variant selection)
      if (priceRangeClone instanceof HTMLElement) {
        const existingRange = currentPrice.querySelector('.price-default__range');
        const mainPriceElement = currentPrice.querySelector('.price-default__main');
        
        if (!existingRange && mainPriceElement instanceof HTMLElement) {
          // Insert price range before the main price element
          mainPriceElement.insertAdjacentElement('beforebegin', priceRangeClone);
        } else if (existingRange) {
          // If range exists in new content, keep it (it should be the same anyway)
          // But we could replace it with the original if needed for consistency
        }
      }

      // Hide/show Budget Pay based on setting
      const budgetPayElement = currentPrice.querySelector('[data-budget-pay]');
      if (budgetPayElement) {
        budgetPayElement.style.display = showBudgetPay ? '' : 'none';
      }

      // Hide/show Discount Percentage based on setting
      const discountElement = currentPrice.querySelector('[data-discount]');
      if (discountElement) {
        discountElement.style.display = showDiscountPercentage ? '' : 'none';
      }

      // Copy other data attributes from newPrice (if morph didn't copy them)
      if (newPrice.dataset.installments) {
        currentPrice.dataset.installments = newPrice.dataset.installments;
      }
      if (newPrice.dataset.price) {
        currentPrice.dataset.price = newPrice.dataset.price;
      }
      if (newPrice.dataset.compareAtPrice) {
        currentPrice.dataset.compareAtPrice = newPrice.dataset.compareAtPrice;
      }
      if (newPrice.dataset.variantId) {
        currentPrice.dataset.variantId = newPrice.dataset.variantId;
      }
    });
  }
  /**
   * Update badges display
   * @param {Document} html
   */
  #updateBadges(html) {
    // Try to find badges in the section rendering response
    const newBadges = html?.querySelector('.product-default-badges');
    
    if (!newBadges) {
      // If not found in section rendering, try to find it in the product-default-card wrapper
      const productCard = html?.querySelector('product-default-card');
      if (productCard) {
        const badgesInCard = productCard.querySelector('.product-default-badges');
        if (badgesInCard) {
          this.#morphBadgesElement(badgesInCard);
          return;
        }
      }
      // If no badges in response, remove existing badges from gallery
      const gallery = this.gallery;
      if (gallery) {
        const currentBadges = gallery.querySelector('.product-default-badges');
        if (currentBadges) {
          currentBadges.remove();
        }
      }
      return;
    }

    this.#morphBadgesElement(newBadges);
  }

  /**
   * Morph the badges element with new content
   * @param {HTMLElement} newBadges - The new badges element from the response
   */
  #morphBadgesElement(newBadges) {
    // Badges are rendered inside the gallery block
    const gallery = this.gallery;
    if (!gallery) return;

    const currentBadges = /** @type {HTMLElement | null} */ (gallery.querySelector('.product-default-badges'));
    
    if (!currentBadges) {
      // If badges don't exist, append them to the gallery
      if (newBadges instanceof HTMLElement) {
        gallery.insertAdjacentElement('afterbegin', newBadges.cloneNode(true));
      }
      return;
    }

    // Use morph to update the badges element
    if (newBadges instanceof HTMLElement) {
      morph(currentBadges, newBadges);
    }
  }

  /**
   * Update shipping badge display
   * @param {Document} html
   */
  #updateShippingBadge(html) {
    // Find the shippingbadge-block wrapper in current card (always exists when block is added)
    const shippingBadgeBlock = /** @type {HTMLElement | null} */ (this.querySelector('.shippingbadge-block'));
    
    if (!shippingBadgeBlock) {
      // If block wrapper doesn't exist, nothing to update
      return;
    }

    // Try to find shipping badge in the section rendering response
    // It could be inside [data-shipping-badge] wrapper or directly in product-default-card
    const productCard = html?.querySelector('product-default-card');
    const dataWrapper = html?.querySelector('[data-shipping-badge]');
    const newShippingBadge = dataWrapper?.querySelector('.shippingbadge') 
      || productCard?.querySelector('.shippingbadge')
      || html?.querySelector('.shippingbadge');
    
    const currentShippingBadge = /** @type {HTMLElement | null} */ (shippingBadgeBlock.querySelector('.shippingbadge'));
    
    if (!newShippingBadge) {
      // If no shipping badge in response, remove existing badge from block wrapper
      if (currentShippingBadge) {
        currentShippingBadge.remove();
      }
      return;
    }

    // Update or add the shipping badge inside the block wrapper
    if (!currentShippingBadge) {
      // Badge doesn't exist in current card, append it inside the block wrapper
      if (newShippingBadge instanceof HTMLElement) {
        shippingBadgeBlock.appendChild(newShippingBadge.cloneNode(true));
      }
    } else {
      // Badge exists, morph it with new content
      if (newShippingBadge instanceof HTMLElement) {
        morph(currentShippingBadge, newShippingBadge);
      }
    }
  }


  /**
   * Update product title based on variant metafield name
   * Supports both product-title and product-title-default block structures
   * @param {Document} html
   * @param {any | null} variantResource
   */
  #updateProductTitle(html, variantResource) {
    const productTitleLink = this.refs.productTitleLink;
    if (!productTitleLink) return;

    // Find new title link in HTML response
    const productCard = html?.querySelector('product-default-card');
    const newTitleLink = productCard?.querySelector('.product-title-link, .product-title-default__link') 
      || html?.querySelector('.product-title-link, .product-title-default__link');

    // Update link href
    if (newTitleLink instanceof HTMLAnchorElement && productTitleLink instanceof HTMLAnchorElement) {
      productTitleLink.href = newTitleLink.href;
    }

    // Extract title from HTML response
    let newTitle = '';
    if (newTitleLink) {
      const titleTextElement = newTitleLink.querySelector('.product-title-text');
      if (titleTextElement) {
        const paragraph = titleTextElement.querySelector('p');
        newTitle = paragraph?.textContent || '';
      } else {
        newTitle = newTitleLink.textContent || '';
      }
    }

    // Fallback: get from variant resource metafields
    if (!newTitle && variantResource?.metafields?.custom) {
      const { name, variant_name } = variantResource.metafields.custom;
      newTitle = name?.value?.trim() || variant_name?.value?.trim() || '';
    }

    // Final fallback: use product title
    if (!newTitle && productTitleLink instanceof HTMLElement) {
      newTitle = productTitleLink.dataset.productTitle || '';
    }

    // Update title text
    if (newTitle) {
      if (productTitleLink.classList.contains('product-title-default__link')) {
        productTitleLink.textContent = newTitle;
      } else {
        const productTitleText = this.querySelector('.product-title-text');
        const paragraph = productTitleText?.querySelector('p') || productTitleText?.querySelector('.text-block p');
        if (paragraph) {
          paragraph.textContent = newTitle;
        }
      }
    }
  }

  /**
   * Update swatch selected visual state
   */
  #updateSwatchSelectedState() {
    if (!this.variantPicker) return;

    const allSwatches = this.querySelectorAll('.swatches-default__swatch');
    const selectedInput = this.variantPicker.selectedOption;

    allSwatches.forEach((swatch) => {
      swatch.classList.remove('swatches-default__swatch--selected');
    });

    if (selectedInput) {
      const selectedLabel = selectedInput.closest('.swatches-default__swatch');
      if (selectedLabel) {
        selectedLabel.classList.add('swatches-default__swatch--selected');
      }
    }
  }

  /**
   * Manually update ALL product-form-components for current card only
   * This prevents the variant update event from affecting other cards' buttons
   * @param {Document} html - The HTML response from the variant update
   * @param {any | null} variantResource - The variant resource from the event
   */
  #updateProductFormComponent(html, variantResource) {
    // Find ALL product-form-components within this card (gallery and main content)
    const productFormComponents = /** @type {NodeListOf<any>} */ (this.querySelectorAll('product-form-component'));
    if (!productFormComponents || productFormComponents.length === 0) return;

    // Extract variant ID from HTML if variantResource is not available
    let variantId = variantResource?.id;
    if (!variantId && html) {
      const newVariantInput = html.querySelector('input[name="id"]');
      if (newVariantInput) {
        variantId = newVariantInput.value;
      }
    }

    // Update each product-form-component instance
    productFormComponents.forEach((productFormComponent) => {
      // Update variant ID - ensure it's set even if variantResource is missing
      const variantIdRef = productFormComponent.refs?.variantId;
      if (variantIdRef && variantId) {
        variantIdRef.value = variantId;
      }

      // Find the add-to-cart button container
      const addToCartButtonContainer = productFormComponent.refs?.addToCartButtonContainer;
      const currentAddToCartButton = addToCartButtonContainer?.refs?.addToCartButton;
      const acceleratedCheckoutButtonContainer = productFormComponent.refs?.acceleratedCheckoutButtonContainer;

      if (!addToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) {
        return;
      }

      // Update button state based on variant availability
      // Only disable if variant is explicitly unavailable
      // Check variantResource first, then fallback to HTML button state
      let shouldDisable = false;
      
      if (variantResource != null) {
        // We have variant resource - check availability directly
        if (variantResource.available == false) {
          // Variant is explicitly unavailable
          shouldDisable = true;
        } else {
          // Variant is available (available is true or truthy)
          shouldDisable = false;
        }
      } else if (html) {
        // No variantResource - check HTML response button state
        const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
        if (newAddToCartButton && newAddToCartButton.hasAttribute('disabled')) {
          // Button in HTML is disabled - variant is unavailable
          shouldDisable = true;
        } else {
          // Button in HTML is not disabled - variant is available (or default state)
          shouldDisable = false;
        }
      } else {
        // No variantResource and no HTML - don't change button state
        // Keep current state (don't disable or enable)
        return;
      }

      // Update button state
      if (shouldDisable) {
        addToCartButtonContainer.disable();
        if (acceleratedCheckoutButtonContainer) {
          acceleratedCheckoutButtonContainer.setAttribute('hidden', 'true');
        }
        
        // Disable Shop Pay button wrapper (for product cards)
        // Shop Pay button is a sibling of product-form-component, so search in the product card
        const shopPayWrapper = this.querySelector('.card-gallery-default__shop-pay-wrapper, .buy-shop-button__shoppay');
        if (shopPayWrapper instanceof HTMLElement) {
          shopPayWrapper.classList.add('card-gallery-default__shop-pay-wrapper--disabled', 'buy-shop-button__shoppay--disabled');
          // Also disable the actual Shop Pay button if it exists
          const shopPayButton = shopPayWrapper.querySelector('shopify-accelerated-checkout, button.shopify-payment-button__button');
          if (shopPayButton instanceof HTMLElement) {
            if (shopPayButton instanceof HTMLButtonElement) {
              shopPayButton.disabled = true;
            } else {
              // For web components, set pointer-events and opacity
              shopPayButton.style.pointerEvents = 'none';
              shopPayButton.style.opacity = '0.5';
            }
          }
        }
      } else {
        // Enable button if variant is available
        addToCartButtonContainer.enable();
        if (acceleratedCheckoutButtonContainer) {
          acceleratedCheckoutButtonContainer.removeAttribute('hidden');
        }
        
        // Enable Shop Pay button wrapper (for product cards)
        // Shop Pay button is a sibling of product-form-component, so search in the product card
        const shopPayWrapper = this.querySelector('.card-gallery-default__shop-pay-wrapper, .buy-shop-button__shoppay');
        if (shopPayWrapper instanceof HTMLElement) {
          shopPayWrapper.classList.remove('card-gallery-default__shop-pay-wrapper--disabled', 'buy-shop-button__shoppay--disabled');
          // Also enable the actual Shop Pay button if it exists
          const shopPayButton = shopPayWrapper.querySelector('shopify-accelerated-checkout, button.shopify-payment-button__button');
          if (shopPayButton instanceof HTMLElement) {
            if (shopPayButton instanceof HTMLButtonElement) {
              shopPayButton.disabled = false;
            } else {
              // For web components, restore pointer-events and opacity
              shopPayButton.style.pointerEvents = '';
              shopPayButton.style.opacity = '';
            }
          }
        }
      }

      // Update button text based on availability
      const textEl = productFormComponent.querySelector('[data-add-text]');
      if (textEl instanceof HTMLElement) {
        if (shouldDisable) {
          textEl.textContent = /** @type {any} */ (window.theme)?.strings?.soldOut || 'Sold Out';
        } else {
          textEl.textContent = /** @type {any} */ (window.theme)?.strings?.addToCart || 'Add To Cart';
        }
      }

      // Morph the button if new button HTML is available
      if (currentAddToCartButton && html) {
        const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
        if (newAddToCartButton) {
          morph(currentAddToCartButton, newAddToCartButton);
        }
      }
    });
  }

  /**
   * Preview a variant (for hover) - called by declarative event system
   * Shows first image (index 0) of hovered variant
   * @param {string} mediaId - The media ID to preview (format: "metafield-{variantId}-0" or media.id)
   */
  previewVariant(mediaId) {
    if (!this.gallery || !mediaId) return;

    // Ensure mediaId is a string (Component framework may pass it as different types)
    mediaId = String(mediaId);

    // Allow hover even after navigation/selection
    this.#navigationUsed = false;

    // Store hovered media ID
    this.#hoveredSwatchMediaId = mediaId;

    // Cancel any pending reset
    if (this.resetVariant && typeof this.resetVariant.cancel === 'function') {
      this.resetVariant.cancel();
    }

    // Extract variant ID from the swatch input element
    const variantPicker = this.variantPicker;
    if (!variantPicker) return;

    const swatchInput = variantPicker.querySelector(`input[data-option-media-id="${mediaId}"]`);
    if (!swatchInput || !swatchInput.dataset.variantId) return;

    // Show first image (index 0) of the hovered variant
    this.#showSlideByMediaIdForHover(swatchInput.dataset.variantId, true);
  }

  /**
   * Show slide by variant ID for swatch hover (not selection)
   * Shows first image (index 0) of the variant
   * @param {string} variantId - The variant ID
   * @param {boolean} useVariantId - Whether to use variant ID or media ID
   */
  #showSlideByMediaIdForHover(variantId, useVariantId = false) {
    let slideToShow = null;

    if (useVariantId && variantId) {
      // Get first image (index 0) of this variant
      const variantSlides = this.#getSlidesForVariant(variantId);
      
      if (variantSlides.length > 0) {
        // Find slide with image index 0 (first image)
        slideToShow = variantSlides.find(slide => 
          parseInt(slide.dataset.imageIndex || '0', 10) === 0
        );
        // If no slide with index 0, use first slide
        if (!slideToShow) {
          slideToShow = variantSlides[0];
        }
      }
    }

    if (slideToShow) {
      // Remove selected classes from all slides during hover
      this.slides.forEach((slide) => {
        slide.classList.remove(
          'card-gallery-default__slide--swatch-hovered',
          'card-gallery-default__slide--card-hover',
          'card-gallery-default__slide--swatch-selected'
        );
        slide.hidden = true;
      });

      // Show only the hovered slide
      slideToShow.removeAttribute('hidden');
      slideToShow.hidden = false;
      slideToShow.classList.add('card-gallery-default__slide--swatch-hovered');
      
      // Force a reflow to ensure CSS is applied
      void slideToShow.offsetHeight;
    }
  }

  /**
   * Clear swatch hover state
   */
  #clearSwatchHoverState() {
    this.#hoveredSwatchMediaId = null;
    this.slides.forEach((slide) => {
      slide.classList.remove('card-gallery-default__slide--swatch-hovered');
      slide.classList.remove('card-gallery-default__slide--card-hover');
    });
  }

  /**
   * Mark swatch as selected (public method for swatch component)
   * Shows first image (index 0) of selected variant
   * @param {string} mediaId - The media ID of the selected variant (format: "metafield-{variantId}-0" or media.id)
   * @param {string} [variantId] - Optional variant ID (if not provided, extracted from mediaId)
   */
  markSwatchSelected(mediaId, variantId = null) {
    this.#swatchSelected = true;
    this.#navigationUsed = false; // Clear navigation state
    this.#clearSwatchHoverState();
    
    // Extract variant ID if not provided
    let extractedVariantId = variantId;
    if (!extractedVariantId && mediaId) {
      if (mediaId.startsWith('metafield-')) {
        // Format: "metafield-{variantId}-0"
        const parts = mediaId.split('-');
        if (parts.length >= 2) {
          extractedVariantId = parts[1]; // Get variant ID
        }
      } else {
        // Regular media ID - find slide and get variant ID
        const slide = this.slides.find(s => String(s.dataset.mediaId) === String(mediaId));
        if (slide && slide.dataset.variantId) {
          extractedVariantId = slide.dataset.variantId;
        }
      }
    }

    // Set selected variant ID
    if (extractedVariantId) {
      this.#selectedVariantId = extractedVariantId;
      
      // Show first image (index 0) of selected variant
      const variantSlides = this.#getSlidesForVariant(extractedVariantId);
      const firstSlide = variantSlides.find(slide => 
        parseInt(slide.dataset.imageIndex || '0', 10) === 0
      );

      if (firstSlide) {
        // Hide all slides
        this.slides.forEach(slide => {
          slide.hidden = true;
          slide.classList.remove(
            'card-gallery-default__slide--active',
            'card-gallery-default__slide--swatch-selected',
            'card-gallery-default__slide--swatch-hovered',
            'card-gallery-default__slide--card-hover'
          );
        });

        // Show first slide of selected variant
        firstSlide.hidden = false;
        firstSlide.classList.add('card-gallery-default__slide--active', 'card-gallery-default__slide--swatch-selected');

        // Update dots for this variant's slides
        this.#updateDotsForVariant(extractedVariantId);
      }
    } else if (mediaId) {
      // Fallback to old behavior
      this.#showSlideByMediaId(mediaId, true);
    }
  }

  /**
   * Update navigation dots for selected variant's slides
   * @param {string} variantId
   */
  #updateDotsForVariant(variantId) {
    const variantSlides = this.#getSlidesForVariant(variantId);
    const dots = this.querySelectorAll('.card-gallery-default__dot');
    
    // Get current active slide's image index
    const currentSlide = variantSlides.find(slide => !slide.hidden);
    const currentImageIndex = currentSlide ? parseInt(currentSlide.dataset.imageIndex || '0', 10) : 0;
    
    // Update dots - only show dots for variant's images
    dots.forEach((dot, index) => {
      // Hide dots beyond variant's image count
      if (index >= variantSlides.length) {
        dot.hidden = true;
        dot.classList.remove('card-gallery-default__dot--active');
        dot.setAttribute('aria-selected', 'false');
      } else {
        dot.hidden = false;
        const isActive = index === currentImageIndex;
        dot.classList.toggle('card-gallery-default__dot--active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      }
    });
  }

  /**
   * Check if navigation was used (public method)
   */
  get navigationUsed() {
    return this.#navigationUsed;
  }

  /**
   * Check if swatch is selected (public method)
   */
  get swatchSelected() {
    return this.#swatchSelected;
  }

  /**
   * Reset to selected variant image (internal)
   */
  #resetVariant = () => {
    // Clear hover state
    this.#clearSwatchHoverState();
    
    // Allow reset even after navigation - navigation flag doesn't prevent reset

    // If swatch is selected, show selected variant's first image
    if (this.#swatchSelected) {
      const selectedVariantId = this.#getSelectedVariantId();
      if (selectedVariantId) {
        const variantSlides = this.#getSlidesForVariant(selectedVariantId);
        const firstSlide = variantSlides.find(slide => 
          parseInt(slide.dataset.imageIndex || '0', 10) === 0
        ) || variantSlides[0];

        if (firstSlide) {
          // Hide all slides
          this.slides.forEach(slide => {
            slide.hidden = true;
            slide.classList.remove(
              'card-gallery-default__slide--active',
              'card-gallery-default__slide--swatch-selected',
              'card-gallery-default__slide--swatch-hovered',
              'card-gallery-default__slide--card-hover'
            );
          });

          // Show first slide of selected variant
          firstSlide.hidden = false;
          firstSlide.classList.add('card-gallery-default__slide--active', 'card-gallery-default__slide--swatch-selected');

          // Update dots for this variant's slides
          this.#updateDotsForVariant(selectedVariantId);
          return;
        }
      }
    }

    // Otherwise show first slide
    this.#showSlideByIndex(0);
  };

  /**
   * Debounced reset variant (for hover leave) - called by declarative event system
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

/**
 * Swatches Variant Picker Component
 * Extends VariantPicker to add swatch-specific hover and selection behavior
 *
 * @extends {VariantPicker}
 */
class SwatchesDefaultVariantPickerComponent extends VariantPicker {
  /** @type {ProductDefaultCard | null} */
  parentProductCard = null;

  /** @type {string | null} */
  pendingVariantId = null;

  /** @type {Map<HTMLElement, EventListener>} - Store swatch click handlers for cleanup */
  #swatchClickHandlers = new Map();

  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-default-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));

    // Ensure initial selected state is correct
    this.#updateSelectedClassFromChecked();
    
    // Initialize swatch visibility immediately (synchronously) to prevent flash
    // Run before first paint to avoid showing all swatches then hiding them
    this.#initializeSwatchVisibility();

    // CRITICAL: Add explicit click handlers for iOS compatibility
    // iOS Safari has issues with labels containing visually-hidden inputs
    // The change event may not fire properly when tapping on labels
    this.#initializeSwatchClickHandlers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up swatch click handlers
    this.#removeSwatchClickHandlers();
  }

  /**
   * Initialize touch/click handlers on swatch labels for iOS compatibility
   * iOS Safari has two issues:
   * 1. First tap triggers pointerenter (hover simulation), second tap triggers click
   * 2. Change events on hidden radio inputs may not fire properly
   * 
   * Solution: Use touchend event to bypass iOS hover simulation and trigger selection immediately
   */
  #initializeSwatchClickHandlers() {
    const swatchLabels = this.querySelectorAll('label.swatches-default__swatch');
    
    // Detect iOS device
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    swatchLabels.forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      
      // Skip if handler already attached
      if (this.#swatchClickHandlers.has(label)) return;
      
      /** @type {EventListener} */
      const touchEndHandler = (e) => {
        // Only handle single touch
        const touchEvent = /** @type {TouchEvent} */ (e);
        if (touchEvent.changedTouches && touchEvent.changedTouches.length !== 1) return;
        
        const input = label.querySelector('input[type="radio"]');
        if (!(input instanceof HTMLInputElement)) return;
        
        // If input is already checked (same swatch clicked again), do nothing
        if (input.checked && input.hasAttribute('data-current-checked')) return;
        
        // CRITICAL: Prevent default to stop iOS from triggering hover simulation
        // This prevents the double-tap issue
        e.preventDefault();
        e.stopPropagation();
        
        // Explicitly check the radio button
        input.checked = true;
        
        // Dispatch change event manually
        const changeEvent = new Event('change', { bubbles: true, cancelable: true });
        input.dispatchEvent(changeEvent);
      };
      
      /** @type {EventListener} */
      const clickHandler = (e) => {
        const input = label.querySelector('input[type="radio"]');
        if (!(input instanceof HTMLInputElement)) return;
        
        // If input is already checked (same swatch clicked again), do nothing
        if (input.checked && input.hasAttribute('data-current-checked')) return;
        
        // Explicitly check the radio button
        input.checked = true;
        
        // Dispatch change event manually
        const changeEvent = new Event('change', { bubbles: true, cancelable: true });
        input.dispatchEvent(changeEvent);
      };
      
      if (isIOS) {
        // On iOS: Use touchend to bypass hover simulation and double-tap issue
        // touchend fires immediately on first tap, before iOS's hover delay
        label.addEventListener('touchend', touchEndHandler, { passive: false });
        this.#swatchClickHandlers.set(label, touchEndHandler);
        
        // Add CSS to disable double-tap-to-zoom on this element
        label.style.touchAction = 'manipulation';
      } else {
        // On other devices: Use click event
        label.addEventListener('click', clickHandler);
        this.#swatchClickHandlers.set(label, clickHandler);
      }
    });
  }

  /**
   * Remove swatch click handlers (cleanup)
   */
  #removeSwatchClickHandlers() {
    // Detect iOS device for proper cleanup
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    this.#swatchClickHandlers.forEach((handler, element) => {
      if (isIOS) {
        element.removeEventListener('touchend', /** @type {EventListener} */ (handler));
      } else {
        element.removeEventListener('click', /** @type {EventListener} */ (handler));
      }
    });
    this.#swatchClickHandlers.clear();
  }

  /**
   * Override parent's updateVariantPicker to preserve max_visible_swatches attribute and reinitialize visibility
   * @param {Document} newHtml - The new HTML
   * @returns {{id: string, url: string} | undefined} Information about the new product if it has changed
   */
  updateVariantPicker(newHtml) {
    // Preserve max_visible_swatches before morphing (always preserve existing value)
    const maxVisibleSwatches = this.dataset.maxVisibleSwatches;
    
    // Call parent's updateVariantPicker
    const newProduct = super.updateVariantPicker(newHtml);
    
    // Always restore max_visible_swatches after morphing (preserve from existing component)
    if (maxVisibleSwatches) {
      this.dataset.maxVisibleSwatches = maxVisibleSwatches;
    }
    
    // Reinitialize swatch visibility and click handlers after morphing
    // Use requestAnimationFrame to ensure DOM is fully updated after morphing
    requestAnimationFrame(() => {
      this.#initializeSwatchVisibility();
      
      // CRITICAL: Reinitialize iOS click handlers after morphing
      // Old labels may be replaced by new ones during morph
      this.#removeSwatchClickHandlers();
      this.#initializeSwatchClickHandlers();
    });
    
    return newProduct;
  }

  /**
   * Override parent's variantChanged to use collection_type-based section logic
   * This prevents the parent from using the hardcoded SECTION_ID_MAP
   * @param {Event} event - The change event from radio input
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const selectedOption = event.target;
    const variantId = selectedOption.dataset.variantId;
    const mediaId = selectedOption.dataset.optionMediaId;

    // Call parent's updateSelectedOption for variant picker logic
    this.updateSelectedOption(selectedOption);
    
    // Dispatch variant selected event (for other components listening)
    this.dispatchEvent(
      new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' })
    );

    // Update selected class immediately for visual feedback
    this.#updateSelectedClass(selectedOption);

    // Mark swatch as selected and update gallery image immediately
    if (mediaId && this.parentProductCard) {
      this.parentProductCard.markSwatchSelected(mediaId, variantId);
    }

    if (variantId) {
      // Store the variant ID for URL update after fetch
      this.pendingVariantId = variantId;

      // Build request URL with collection_type logic
      const productUrl = this.dataset.productUrl?.split('?')[0];
      if (!productUrl) return;

      // Get collection type from gallery element
      const collectionType = this.parentProductCard?.gallery?.dataset?.collectionType || 'normal';
      
      // Determine which section to call based on collection type
      const sectionId = collectionType === 'simple' 
        ? 'section-rendering-product-default-simple'
        : 'section-rendering-product-default-normal';

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', sectionId);
      
      // Fetch updated section (max_visible_swatches is preserved in data attribute)
      this.fetchUpdatedSection(url.href);
    }
  }

  /**
   * Update the selected class on swatches
   * @param {HTMLInputElement} selectedInput - The selected input element
   */
  #updateSelectedClass(selectedInput) {
    // Remove selected class from all swatches and update checked states
    this.querySelectorAll('.swatches-default__swatch').forEach((swatch) => {
      swatch.classList.remove('swatches-default__swatch--selected');
      const input = swatch.querySelector('.swatches-default__input');
      if (input) input.removeAttribute('data-current-checked');
    });

    // Add selected class to the clicked swatch's label
    const selectedLabel = selectedInput.closest('.swatches-default__swatch');
    if (selectedLabel) {
      selectedLabel.classList.add('swatches-default__swatch--selected');
    }
    selectedInput.setAttribute('data-current-checked', 'true');
  }

  /**
   * Updates the card URL when a variant is selected.
   * Also updates the selected class after morphing to ensure visual state is correct.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductDefaultCard) {
      const productCardLink = this.parentProductCard.refs.productCardLink;
      if (productCardLink) {
        const currentUrl = new URL(productCardLink.href);
        currentUrl.searchParams.set('variant', this.pendingVariantId);
        productCardLink.href = currentUrl.toString();
      }
      this.pendingVariantId = null;
    }

    // Update selected class after morphing to ensure visual state is correct
    this.#updateSelectedClassFromChecked();
  }

  /**
   * Update selected class based on data-current-checked attribute after morphing
   */
  #updateSelectedClassFromChecked() {
    // Remove selected class from all swatches
    this.querySelectorAll('.swatches-default__swatch').forEach((swatch) => {
      swatch.classList.remove('swatches-default__swatch--selected');
    });

    // Find the input with data-current-checked="true" and add selected class to its label
    const checkedInput = this.querySelector('.swatches-default__input[data-current-checked="true"]');
    const selectedLabel = checkedInput?.closest('.swatches-default__swatch');
    if (selectedLabel) {
      selectedLabel.classList.add('swatches-default__swatch--selected');
    }
  }

  /**
   * Initialize swatch visibility based on max_visible_swatches data attribute
   * Shows/hides swatches and overflow button dynamically
   */
  #initializeSwatchVisibility() {
    // Get swatch list first (early return if not found)
    const swatchList = this.querySelector('.swatches-default__list');
    if (!swatchList) {
      return;
    }
    
    // Get all swatch labels and overflow button (cache for performance)
    const allSwatches = Array.from(swatchList.querySelectorAll('label.swatches-default__swatch'));
    const overflowButton = swatchList.querySelector('button.swatches-default__swatch--overflow');
    
    const totalSwatches = allSwatches.length;
    
    // Early return if no swatches
    if (totalSwatches === 0) {
      return;
    }
    
    // Check if mobile/tablet breakpoint (max-width: 1199px) - matches buy-buttons-default visibility
    // buy-buttons-default is visible up to 1199px, so show all swatches at same breakpoint
    const isMobileOrTablet = window.matchMedia('(max-width: 1199px)').matches;
    
    // Get max_visible_swatches from data attribute, default to 5
    const maxVisibleSwatches = parseInt(this.dataset.maxVisibleSwatches || '5', 10);
    
    // On mobile/tablet: show all swatches and hide overflow button (same breakpoint as buy-buttons-default)
    if (isMobileOrTablet) {
      // Batch DOM updates for better performance
      for (let i = 0; i < totalSwatches; i++) {
        const swatch = allSwatches[i];
        if (swatch instanceof HTMLElement) {
          swatch.removeAttribute('hidden');
        }
      }
      if (overflowButton instanceof HTMLElement) {
        overflowButton.setAttribute('hidden', '');
      }
      return;
    }
    
    // Desktop logic: use max_visible_swatches
    // If total swatches <= max_visible_swatches, show all and hide overflow button
    if (totalSwatches <= maxVisibleSwatches) {
      // Batch DOM updates for better performance
      for (let i = 0; i < totalSwatches; i++) {
        const swatch = allSwatches[i];
        if (swatch instanceof HTMLElement) {
          swatch.removeAttribute('hidden');
        }
      }
      if (overflowButton instanceof HTMLElement) {
        overflowButton.setAttribute('hidden', '');
      }
      return;
    }
    
    // Show first max_visible_swatches swatches, hide the rest
    // Batch DOM updates: first show visible ones, then hide hidden ones
    for (let i = 0; i < totalSwatches; i++) {
      const swatch = allSwatches[i];
      if (swatch instanceof HTMLElement) {
        if (i >= maxVisibleSwatches) {
          swatch.setAttribute('hidden', '');
        } else {
          swatch.removeAttribute('hidden');
        }
      }
    }
    
    // Show overflow button and update count (desktop only)
    if (overflowButton instanceof HTMLElement) {
      const overflowCountSpan = overflowButton.querySelector('.swatches-default__overflow-count');
      if (overflowCountSpan instanceof HTMLElement) {
        const overflowCount = totalSwatches - maxVisibleSwatches;
        overflowCountSpan.textContent = `+${overflowCount}`;
        
        // Update aria-label using default aria-label from data attribute or fallback
        const defaultAriaLabel = overflowButton.dataset.defaultAriaLabel || 'View all options';
        overflowButton.setAttribute('aria-label', `${defaultAriaLabel} (+${overflowCount})`);
      }
      overflowButton.removeAttribute('hidden');
    }
  }
}

// Register custom elements
if (!customElements.get('product-default-card')) {
  customElements.define('product-default-card', ProductDefaultCard);
}

if (!customElements.get('swatches-default-variant-picker-component')) {
  customElements.define('swatches-default-variant-picker-component', SwatchesDefaultVariantPickerComponent);
}
